import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import axios from 'axios';

/**
 * A spreadsheet attachment drawn as the sheet it is: the backend reads the
 * workbook into a grid (text, bold, fills, merged cells, column widths) and this
 * renders it as a plain table. `fallback` is shown when the grid cannot be read.
 */
function SpreadsheetPreview({ t, url, fallback }) {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setFailed(false);
    axios
      .get(url)
      .then((response) => {
        if (!cancelled) setData(response.data);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  if (failed) return fallback;
  if (!data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 280 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  const sheets = data.sheets || [];
  if (sheets.length === 0) return fallback;

  return (
    <Box data-testid="spreadsheet-preview" sx={{ p: 1.5, bgcolor: '#fff', minHeight: '100%' }}>
      {sheets.map((sheet, sheetIndex) => (
        <Box key={`${sheet.name}-${sheetIndex}`} sx={{ mb: 2 }}>
          {sheets.length > 1 && (
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: 'text.secondary', mb: 0.5 }}>
              {sheet.name}
            </Typography>
          )}
          <Box
            component="table"
            sx={{
              borderCollapse: 'collapse',
              tableLayout: 'fixed',
              fontSize: '12px',
              lineHeight: 1.35,
              color: '#1a202c',
              '& td': {
                px: 0.75,
                py: 0.4,
                border: '1px solid #edf0f3',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                verticalAlign: 'bottom',
              },
            }}
          >
            <colgroup>
              {sheet.widths.map((width, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <col key={index} style={{ width }} />
              ))}
            </colgroup>
            <tbody>
              {sheet.rows.map((row, rowIndex) => (
                // eslint-disable-next-line react/no-array-index-key
                <tr key={rowIndex}>
                  {row.map((cell, cellIndex) => (
                    <td
                      // eslint-disable-next-line react/no-array-index-key
                      key={cellIndex}
                      colSpan={cell.span || 1}
                      title={cell.v || undefined}
                      style={{
                        fontWeight: cell.b ? 700 : 400,
                        textAlign: cell.num ? 'right' : 'left',
                        fontVariantNumeric: cell.num ? 'tabular-nums' : undefined,
                        backgroundColor: cell.bg,
                        color: cell.fg,
                        height: 20,
                      }}
                    >
                      {cell.v}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Box>
          {sheet.truncated && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {t('inboundDeliveries.previewTruncated')}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
}

export default SpreadsheetPreview;
